import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDatabase } from '../context/DatabaseContext';
import { useToast } from '../context/ToastContext';
import { Database, Activity, Zap, Globe, RefreshCw, CheckCircle, BarChart3, Clock, TrendingUp, Brain } from 'lucide-react';
import { getSystemStats, getRecentActivity } from '../services/api';
import { formatLocalDate } from '../utils/timezone';

export const Dashboard = () => {
    const { t } = useTranslation(['pages-misc-3', 'common']);
    const { databases, refreshDatabases } = useDatabase();
    const { showToast } = useToast();
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        totalDatabases: 0,
        totalAnalyses: 0,
        totalGeneratedQueries: 0,
        databaseBasedQueries: 0,
        nlpBasedQueries: 0,
        agentBasedQueries: 0,
        validatedQueries: 0,
        failedQueries: 0,
        totalEndpoints: 0,
        activeEndpoints: 0
    });
    const [recentActivity, setRecentActivity] = useState([]);
    const [activityLoading, setActivityLoading] = useState(true);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            setLoading(true);
            try {
                const res = await getSystemStats();
                const systemStats = res.data?.data?.stats || res.data?.data || {};

                setStats({
                    totalDatabases: systemStats.connected_databases || databases.length,
                    totalAnalyses: systemStats.analyzed_schemas || 0,
                    totalGeneratedQueries: systemStats.total_generated_queries || 0,
                    databaseBasedQueries: systemStats.database_based_queries || 0,
                    nlpBasedQueries: systemStats.nlp_based_queries || 0,
                    agentBasedQueries: systemStats.agent_based_queries || 0,
                    validatedQueries: systemStats.validated_queries || 0,
                    failedQueries: systemStats.failed_queries || 0,
                    totalEndpoints: systemStats.total_endpoints || 0,
                    activeEndpoints: systemStats.active_endpoints || 0,
                });
            } catch (error) {
                console.error('Failed to fetch system stats:', error);
                // Fallback to database count if system stats endpoint fails
                setStats({
                    totalDatabases: databases.length,
                    totalAnalyses: 0,
                    totalGeneratedQueries: 0,
                    databaseBasedQueries: 0,
                    nlpBasedQueries: 0,
                    agentBasedQueries: 0,
                    validatedQueries: 0,
                    failedQueries: 0,
                    totalEndpoints: 0,
                    activeEndpoints: 0,
                });
            } finally {
                setLoading(false);
            }
        };

        const fetchRecentActivity = async () => {
            setActivityLoading(true);
            try {
                const res = await getRecentActivity();
                const activityData = res.data?.data?.activity || [];
                setRecentActivity(activityData);
            } catch (error) {
                console.error('Failed to fetch recent activity:', error);
                setRecentActivity([]);
            } finally {
                setActivityLoading(false);
            }
        };

        fetchStats();
        fetchRecentActivity();
    }, [databases.length]);

    const statCards = [
        {
            label: t('dashboard.stats.connectedDatabases'),
            value: stats.totalDatabases,
            icon: Database,
            bgColor: 'bg-primary-50',
            iconColor: 'text-primary-600',
            subtext: t('dashboard.stats.activeConnections')
        },
        {
            label: t('dashboard.stats.schemaAnalyses'),
            value: stats.totalAnalyses,
            icon: Activity,
            bgColor: 'bg-pink-50',
            iconColor: 'text-pink-600',
            subtext: t('dashboard.stats.analysesCompleted')
        },
        {
            label: t('dashboard.stats.generatedQueries'),
            value: stats.totalGeneratedQueries,
            icon: Zap,
            bgColor: 'bg-green-50',
            iconColor: 'text-green-600',
            subtext: t('dashboard.stats.validFailedCount', { valid: stats.validatedQueries, failed: stats.failedQueries }),
            breakdown: [
                { label: t('dashboard.stats.databaseBased'), value: stats.databaseBasedQueries, color: 'text-emerald-600' },
                { label: t('dashboard.stats.nlpBased'), value: stats.nlpBasedQueries, color: 'text-teal-600' },
                { label: t('dashboard.stats.agentBased'), value: stats.agentBasedQueries, color: 'text-cyan-600' }
            ]
        },
        {
            label: t('dashboard.stats.apiEndpoints'),
            value: stats.totalEndpoints,
            icon: Globe,
            bgColor: 'bg-primary-50',
            iconColor: 'text-primary-600',
            subtext: t('dashboard.stats.activeTotalCount', { active: stats.activeEndpoints, total: stats.totalEndpoints }),
            breakdown: [
                { label: t('common:active'), value: stats.activeEndpoints, color: 'text-green-600' },
                { label: t('dashboard.stats.total'), value: stats.totalEndpoints, color: 'text-primary-600' }
            ]
        },
    ];

    const quickActions = [
        {
            label: t('dashboard.quickActions.connectDatabase'),
            icon: Database,
            color: 'purple',
            onClick: () => navigate('/database-connection')
        },
        {
            label: t('dashboard.quickActions.schemaAnalysis'),
            icon: BarChart3,
            color: 'primary',
            onClick: () => navigate('/schema-analysis')
        },
        {
            label: t('dashboard.quickActions.aiAgents'),
            icon: Brain,
            color: 'green',
            onClick: () => navigate('/agents')
        },
        {
            label: t('dashboard.quickActions.queryGenerator'),
            icon: Zap,
            color: 'green',
            onClick: () => navigate('/query-generator')
        },
        {
            label: t('dashboard.quickActions.apiEndpoints'),
            icon: Globe,
            color: 'indigo',
            onClick: () => navigate('/api-endpoints')
        }
    ];

    return (
        <div className="page-content space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <h1 className="page-title">{t('dashboard.title')}</h1>
                    <button
                        onClick={async () => {
                            await refreshDatabases();
                            showToast(t('dashboard.databasesRefreshed'), 'success');
                        }}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-white hover:shadow-sm rounded-lg transition-all border border-gray-200"
                    >
                        <RefreshCw className="w-4 h-4" />
                        {t('common:refresh')}
                    </button>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {statCards.map((stat, index) => (
                        <div
                            key={index}
                            className="bg-white rounded-xl p-5 border border-gray-100 hover:shadow-md transition-shadow"
                        >
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                    <p className="text-sm text-gray-600 mb-1">{stat.label}</p>
                                    <p className="text-3xl font-bold text-gray-900">
                                        {loading ? '...' : stat.value}
                                    </p>
                                </div>
                                <div className={`p-3 ${stat.bgColor} rounded-lg`}>
                                    <stat.icon className={`w-6 h-6 ${stat.iconColor}`} />
                                </div>
                            </div>
                            <p className="text-xs text-gray-500">{stat.subtext}</p>
                            {stat.breakdown && !loading && (
                                <div className="mt-3 pt-3 border-t border-gray-100">
                                    {stat.breakdown.map((item, idx) => (
                                        <div key={idx} className="flex justify-between items-center text-xs">
                                            <span className="text-gray-500">{item.label}</span>
                                            <span className={`font-semibold ${item.color}`}>{item.value}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Quick Actions */}
                <div className="bg-white rounded-xl border border-gray-100 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('dashboard.quickActionsTitle')}</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {quickActions.map((action, index) => {
                            let bgColor, textColor;
                            switch(action.color) {
                                case 'purple':
                                    bgColor = 'bg-primary-100';
                                    textColor = 'text-primary-700';
                                    break;
                                case 'primary':
                                    bgColor = 'bg-primary-100';
                                    textColor = 'text-primary-700';
                                    break;
                                case 'green':
                                    bgColor = 'bg-green-100';
                                    textColor = 'text-green-700';
                                    break;
                                case 'indigo':
                                    bgColor = 'bg-primary-100';
                                    textColor = 'text-primary-700';
                                    break;
                                default:
                                    bgColor = 'bg-gray-100';
                                    textColor = 'text-gray-700';
                            }
                            
                            return (
                                <button
                                    key={index}
                                    onClick={action.onClick}
                                    className="flex flex-col items-center justify-center p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:shadow-sm transition-all relative"
                                >
                                    <div className={`w-12 h-12 ${bgColor} rounded-lg flex items-center justify-center mb-2`}>
                                        <action.icon className={`w-6 h-6 ${textColor}`} />
                                    </div>
                                    <span className="text-sm font-medium text-gray-700">{action.label}</span>
                                    {action.badge && (
                                        <span className="absolute top-2 right-2 px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">
                                            {action.badge}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Recent Activity */}
                    <div className="bg-white rounded-xl border border-gray-100">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                            <div className="flex items-center gap-2">
                                <Clock className="w-5 h-5 text-primary-600" />
                                <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.recentActivity')}</h3>
                            </div>
                        </div>
                        <div className="p-6">
                            {activityLoading ? (
                                <div className="text-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                                    <p className="text-gray-500 mt-4">{t('dashboard.loadingActivity')}</p>
                                </div>
                            ) : recentActivity.length === 0 ? (
                                <div className="text-center py-8">
                                    <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                                    <p className="text-gray-500 mb-2">{t('dashboard.noRecentActivity')}</p>
                                    <p className="text-sm text-gray-400 mb-4">
                                        {databases.length === 0
                                            ? t('dashboard.connectToSeeActivity')
                                            : t('dashboard.performActionsToSeeActivity')}
                                    </p>
                                    {databases.length === 0 && (
                                        <button
                                            onClick={() => navigate('/database-connection')}
                                            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
                                        >
                                            {t('dashboard.connectDatabase')}
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {recentActivity.map((activity, index) => {
                                        // Icon mapping based on entity type
                                        const getIcon = () => {
                                            switch(activity.entity_type) {
                                                case 'database':
                                                    return { icon: Database, bgColor: 'bg-primary-100', iconColor: 'text-primary-600' };
                                                case 'schema_analysis':
                                                    return { icon: Activity, bgColor: 'bg-pink-100', iconColor: 'text-pink-600' };
                                                case 'query':
                                                    return { icon: Zap, bgColor: 'bg-green-100', iconColor: 'text-green-600' };
                                                case 'endpoint':
                                                    return { icon: Globe, bgColor: 'bg-primary-100', iconColor: 'text-primary-600' };
                                                case 'insight':
                                                    // Check if this is an agent insight
                                                    if (activity.agent_type) {
                                                        return { icon: Brain, bgColor: 'bg-amber-100', iconColor: 'text-amber-600' };
                                                    }
                                                    return { icon: TrendingUp, bgColor: 'bg-primary-100', iconColor: 'text-primary-600' };
                                                case 'agent_execution':
                                                    return { icon: Brain, bgColor: 'bg-orange-100', iconColor: 'text-orange-600' };
                                                case 'query_execution':
                                                    return { icon: CheckCircle, bgColor: 'bg-emerald-100', iconColor: 'text-emerald-600' };
                                                case 'data_profile':
                                                    return { icon: BarChart3, bgColor: 'bg-cyan-100', iconColor: 'text-cyan-600' };
                                                default:
                                                    return { icon: Clock, bgColor: 'bg-gray-100', iconColor: 'text-gray-600' };
                                            }
                                        };
                                        
                                        const { icon: Icon, bgColor, iconColor } = getIcon();
                                        
                                        return (
                                            <div
                                                key={index}
                                                className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                            >
                                                <div className={`w-10 h-10 ${bgColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
                                                    <Icon className={`w-5 h-5 ${iconColor}`} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between">
                                                        <h4 className="font-medium text-gray-900 text-sm">
                                                            {activity.action_summary}
                                                        </h4>
                                                        <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
                                                            {formatLocalDate(activity.latest_timestamp)}
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-gray-500 mt-1 truncate">
                                                        {activity.latest_details}
                                                    </p>
                                                    {activity.database_name && (
                                                        <p className="text-xs text-gray-400 mt-1">
                                                            {activity.database_name}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Connected Databases */}
                    <div className="bg-white rounded-xl border border-gray-100">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.connectedDatabases')}</h3>
                            <button
                                onClick={() => navigate('/database-connection')}
                                className="text-sm text-green-600 hover:text-green-700 font-medium"
                            >
                                + {t('dashboard.addNew')}
                            </button>
                        </div>
                        <div className="p-6">
                            {databases.length === 0 ? (
                                <div className="text-center py-8">
                                    <p className="text-gray-500 mb-4">{t('dashboard.noDatabasesConnected')}</p>
                                    <button
                                        onClick={() => navigate('/database-connection')}
                                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
                                    >
                                        {t('dashboard.connectDatabase')}
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {databases.map((db) => (
                                        <div
                                            key={db.id}
                                            className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:shadow-sm transition-all cursor-pointer group"
                                            onClick={() => navigate(`/schema-analysis?db=${db.id}`)}
                                        >
                                            <div className="flex items-start justify-between mb-2">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                                                        <Database className="w-4 h-4 text-primary-600" />
                                                    </div>
                                                    <div>
                                                        <h4 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                                                            {db.name}
                                                        </h4>
                                                        <p className="text-xs text-gray-500 mt-0.5">
                                                            {db.description || t('dashboard.noDescription')}
                                                        </p>
                                                    </div>
                                                </div>
                                                <span className="px-2 py-1 text-xs font-medium bg-primary-50 text-primary-700 rounded">
                                                    {db.db_type || 'PostgreSQL'}
                                                </span>
                                            </div>
                                            <div className="text-xs text-gray-400 mt-2">
                                                {t('dashboard.connectedOn', { date: formatLocalDate(db.created_at) })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
        </div>
    );
};
